import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { Control, Controller, ControllerRenderProps } from 'react-hook-form'
import { useIntl } from 'react-intl'

import s from './styles.module.scss'

import {
  HousesInformationsFieldsFragment,
  TravelTypeEnum,
} from '../../../../../../lib/graphql'
import { breakpoints, useMediaQuery } from '../../../../../../lib/hooks'
import useDateFormatter from '../../../../../../lib/hooks/useDateFormatter'
import {
  convertTimezonedDateToIsoAtMidnight,
  convertTimezonedToIsoWithOneMonthStartOfDay,
} from '../../../../../../utils/date-helpers'
import { Mixpanel } from '../../../../../../utils/Mixpanel'
import { Icon } from '../../../../../atoms'
import { useTravelTypeContext } from '../../../../../contexts'
import { MobileBackPage } from '../../../../../layout'
import DatesCalendar, {
  IDatesCalendarProps,
} from '../../../../../organisms/dates-calendar/dates-calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../popover/Popover'
import {
  ISelectProposalForm,
  ISelectProposalHook,
} from '../../../modal-select-proposal.types'

interface ICalendarProps
  extends Omit<IDatesCalendarProps, 'houseAvailabilities'> {
  startDate?: Date
  endDate?: Date
  setFieldValue?: (value: ISelectProposalForm['dates']) => void
}

export interface DatesPickerProps {
  control: Control<ISelectProposalForm, any>
  dates: ISelectProposalForm['dates']
  house: Pick<
    HousesInformationsFieldsFragment,
    '_id' | 'arrival' | 'departure' | 'capacity'
  >
  isFirstRender: boolean
  getHouseAvailabilitiesFromTo: ISelectProposalHook['getHouseAvailabilitiesFromTo']
  houseAvailabilities: ISelectProposalHook['houseAvailabilities']
}

const DatesPicker = ({
  control,
  house,
  dates,
  isFirstRender,
  getHouseAvailabilitiesFromTo,
  houseAvailabilities,
}: DatesPickerProps) => {
  const intl = useIntl()
  const dateFormatter = useDateFormatter()
  const { travelType } = useTravelTypeContext()
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [activeStartDate, setActiveStartDate] = useState<Date>(new Date())
  const [calendarProps, setCalendarProps] = useState<ICalendarProps>({
    activeStartDate,
    setActiveStartDate,
    startDate: dates.startDate,
    endDate: dates.endDate,
  })

  const isMobile = useMediaQuery(breakpoints.newMobile)

  const allowMultiDates =
    travelType === TravelTypeEnum.NIGHTLY_TRIP ||
    travelType === TravelTypeEnum.DAILY_TRIP

  useEffect(() => {
    getHouseAvailabilitiesFromTo({
      inputDto: {
        houseId: house?._id,
        from: convertTimezonedDateToIsoAtMidnight(activeStartDate),
        to: convertTimezonedToIsoWithOneMonthStartOfDay(activeStartDate),
      },
    })
  }, [activeStartDate])

  useEffect(() => {
    if (!isFirstRender) {
      setCalendarProps({
        ...calendarProps,
        startDate: undefined,
        endDate: undefined,
      })
    }
  }, [travelType])

  const openCalendar = (
    field: ControllerRenderProps<ISelectProposalForm, 'dates'>,
  ) => {
    setCalendarProps({
      ...calendarProps,
      setFieldValue: (value) => field.onChange(value),
    })
    setIsCalendarOpen(true)
  }

  return (
    <Popover onOpenChange={setIsCalendarOpen} open={isCalendarOpen}>
      <Controller
        control={control}
        name='dates'
        rules={{
          required: intl.formatMessage({
            id: 'ui.DatesPicker.incompleteField',
            defaultMessage: 'Champ incomplet',
          }),
          validate: (value) =>
            value.startDate && value.endDate
              ? true
              : intl.formatMessage({
                  id: 'ui.DatesPicker.incompleteField',
                  defaultMessage: 'Champ incomplet',
                }),
        }}
        render={({ field, fieldState: { error } }) => {
          return (
            <PopoverTrigger
              className={clsx(s.datePart)}
              onClick={() => !isCalendarOpen && openCalendar(field)}
              data-testid='dates-picker'
            >
              <div
                className={s.dateAdultsPart}
                data-gtm='departure-date-calendar'
                onClick={() => Mixpanel.track('departure-date-calendar')}
              >
                <p className={clsx({ [s.error]: Boolean(error) })}>
                  {calendarProps.startDate
                    ? dateFormatter.format({
                        date: field?.value?.startDate,
                        formatString: 'DD/MM/YYYY',
                        useUTC: true,
                      })
                    : '-'}
                </p>
              </div>
              {allowMultiDates && (
                <>
                  <span className={s.spacer} />
                  <div
                    className={s.dateAdultsPart}
                    data-gtm='return-date-calendar'
                  >
                    <p className={clsx({ [s.error]: Boolean(error) })}>
                      {calendarProps.endDate
                        ? dateFormatter.format({
                            date: field?.value?.endDate,
                            formatString: 'DD/MM/YYYY',
                            useUTC: true,
                          })
                        : '-'}
                    </p>
                  </div>
                </>
              )}
            </PopoverTrigger>
          )
        }}
      />
      {isMobile ? (
        isCalendarOpen && (
          <MobileBackPage
            backPageProps={{
              title: '',
            }}
            isModal={true}
            style={{ zIndex: 9999999999 }}
            navBar={
              <div className={s.devisFormNavbar}>
                <div className={s.navbarTitle}>Sélectionnez vos dates</div>
                <div
                  className={s.closeModal}
                  onClick={() => {
                    setIsCalendarOpen(false)
                  }}
                >
                  <Icon
                    name='cross-without-circle'
                    color='black'
                    height='12px'
                    width='12px'
                  />
                </div>
              </div>
            }
          >
            <PopoverContent>
              <div className={clsx(s.dateCalendarContainerMobile)}>
                <DatesCalendar
                  startDate={calendarProps.startDate}
                  endDate={calendarProps.endDate}
                  setDate={({ startDate, endDate }) => {
                    if (calendarProps.setFieldValue) {
                      calendarProps.setFieldValue({ startDate, endDate })
                    }
                    setCalendarProps({
                      ...calendarProps,
                      startDate,
                      endDate,
                    })
                  }}
                  closeModal={() => {
                    setIsCalendarOpen(false)
                  }}
                  house={house}
                  activeStartDate={activeStartDate}
                  setActiveStartDate={setActiveStartDate}
                  houseAvailabilities={houseAvailabilities}
                />
              </div>
            </PopoverContent>
          </MobileBackPage>
        )
      ) : (
        <PopoverContent>
          <div className={s.dateCalendarContainer}>
            <div>
              <DatesCalendar
                startDate={calendarProps.startDate}
                endDate={calendarProps.endDate}
                setDate={({ startDate, endDate }) => {
                  if (calendarProps.setFieldValue) {
                    calendarProps.setFieldValue({ startDate, endDate })
                  }
                  setCalendarProps({
                    ...calendarProps,
                    startDate,
                    endDate,
                  })
                }}
                closeModal={() => {
                  setIsCalendarOpen(false)
                }}
                house={house}
                activeStartDate={activeStartDate}
                setActiveStartDate={setActiveStartDate}
                houseAvailabilities={houseAvailabilities}
              />
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  )
}

export default DatesPicker
